
import { useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

interface UseFormPersistenceOptions {
  storageKey: string;
  excludeFields?: string[];
  useSessionStorage?: boolean;
}

interface RestoredDraftInfo {
  savedAt: number;
  fieldCount: number;
}

export const useFormPersistence = <T extends Record<string, any>>(
  form: UseFormReturn<T>,
  options: UseFormPersistenceOptions
) => {
  const hasRestoredRef = useRef(false);
  const [availableDraft, setAvailableDraft] = useState<RestoredDraftInfo | null>(null);

  const getStorage = () => (options.useSessionStorage ? sessionStorage : localStorage);

  // Détecter immédiatement la présence d'un brouillon (sans le restaurer)
  useEffect(() => {
    try {
      const raw = getStorage().getItem(options.storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      // Compat retro : ancien format = valeurs directes, nouveau = { values, savedAt }
      const values = parsed?.values ?? parsed;
      const savedAt = parsed?.savedAt ?? null;
      const fieldCount = Object.values(values || {}).filter(
        (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)
      ).length;
      if (fieldCount > 0 && savedAt) {
        setAvailableDraft({ savedAt, fieldCount });
      }
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveFormData = () => {
    try {
      const formData = form.getValues();
      const dataToSave: any = { ...formData };

      if ('isTTC' in dataToSave) delete dataToSave.isTTC;
      if ('isSubjectToVAT' in dataToSave) delete dataToSave.isSubjectToVAT;

      // Nouveau format : { values, savedAt } pour permettre l'affichage d'un bandeau
      const payload = { values: dataToSave, savedAt: Date.now() };
      getStorage().setItem(options.storageKey, JSON.stringify(payload));
    } catch (error) {
      console.error('Error saving form data:', error);
    }
  };

  const restoreFormData = () => {
    try {
      const savedData = getStorage().getItem(options.storageKey);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        const data = parsed?.values ?? parsed;

        const currentFormValues = form.getValues();

        Object.keys(data).forEach((key) => {
          if ((key === 'isTTC' || key === 'isSubjectToVAT') &&
              typeof currentFormValues[key as keyof typeof currentFormValues] !== 'undefined') {
            return;
          }
          if (options.excludeFields && options.excludeFields.includes(key)) {
            return;
          }
          form.setValue(key as any, data[key]);
        });
        setAvailableDraft(null);
        hasRestoredRef.current = true;
        return true;
      }
    } catch (error) {
      console.error('Error restoring form data:', error);
    }
    return false;
  };

  const clearSavedData = () => {
    getStorage().removeItem(options.storageKey);
    setAvailableDraft(null);
    try {
      form.reset();
    } catch {
      // ignore
    }
  };

  const dismissDraftBanner = () => {
    setAvailableDraft(null);
  };

  const forceRestore = () => {
    restoreFormData();
  };

  return {
    saveFormData,
    restoreFormData,
    clearSavedData,
    forceRestore,
    availableDraft,
    dismissDraftBanner,
  };
};

export type { UseFormPersistenceOptions };
